<template>
  <!-- Bill Generated Success Modal -->
  <div class="modal fade" id="bill-generated-modal" tabindex="-1" role="dialog" aria-labelledby="billGeneratedModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
      <div class="modal-content">
        <!-- Header -->
        <div class="modal-header bg-success text-white border-0">
          <div class="d-flex align-items-center">
            <i class="mdi mdi-check-circle font-size-32 me-3"></i>
            <div>
              <h5 class="modal-title mb-0" id="billGeneratedModalLabel">Invoice Generated Successfully</h5>
              <small class="opacity-75">Your invoice has been created</small>
            </div>
          </div>
          <button type="button" class="btn-close btn-close-white" data-dismiss="modal" aria-label="Close"></button>
        </div>

        <!-- Body -->
        <div class="modal-body p-4">

          <!-- ✅ NEW: Pay Later Notification Banner -->
          <div v-if="payLater" class="alert alert-info border-info d-flex align-items-center mb-4" role="alert">
            <i class="mdi mdi-send-check font-size-24 me-3"></i>
            <div class="flex-grow-1">
              <h6 class="mb-1">Invoice Sent to Customer</h6>
              <p class="mb-0 font-size-13">
                <span v-if="customerPhone">
                  <i class="mdi mdi-cellphone me-1"></i> SMS sent to <strong>{{ customerPhone }}</strong>
                </span>
                <span v-if="customerPhone && customerEmail"> | </span>
                <span v-if="customerEmail">
                  <i class="mdi mdi-email me-1"></i> Email sent to <strong>{{ customerEmail }}</strong>
                </span>
              </p>
            </div>
          </div>

          <!-- Success Message Banner -->
          <div class="alert alert-success border-success d-flex align-items-center mb-4" role="alert">
            <i class="mdi mdi-check-circle-outline font-size-20 me-3"></i>
            <div class="flex-grow-1">
              <h6 class="mb-0">{{ message || 'Bill Generated Successfully!' }}</h6>
            </div>
          </div>

          <!-- Approval Warning (if printable is false) -->
          <div v-if="billDetails.printable === 'false'" class="alert alert-warning border-warning mb-4" role="alert">
            <div class="d-flex align-items-start">
              <i class="mdi mdi-alert font-size-24 me-3"></i>
              <div>
                <h6 class="alert-heading mb-2">Pending Approval</h6>
                <p class="mb-0">This invoice requires approval before it can be printed or paid. Please contact your supervisor or wait for automatic approval.</p>
              </div>
            </div>
          </div>

          <!-- Invoice Details Card -->
          <div class="card border shadow-sm mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0 text-uppercase">
                <i class="mdi mdi-receipt me-2"></i>Invoice Information
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <!-- Invoice Number -->
                <div class="col-md-4 mb-3">
                  <label class="text-muted mb-1 font-13px">Invoice Number:</label>
                  <div class="d-flex align-items-center">
                    <h5 class="mb-0 text-primary fw-bold">
                      {{ maskBillNo(billDetails.billNo, billDetails.printable) }}
                    </h5>
                    <button
                        v-if="billDetails.printable === 'true'"
                        @click="copyToClipboard(billDetails.billNo)"
                        class="btn btn-sm btn-soft-primary ms-2"
                        title="Copy Invoice Number">
                      <i class="mdi mdi-content-copy"></i>
                    </button>
                  </div>
                </div>

                <!-- Amount -->
                <div class="col-md-4 mb-3">
                  <label class="text-muted mb-1 font-13px">Total Amount:</label>
                  <h5 class="mb-0 text-success fw-bold">KES {{ numberFormat(billDetails.detailAmount) }}</h5>
                </div>

                <!-- Status -->
                <div class="col-md-4 mb-3">
                  <label class="text-muted mb-1 font-13px">Status:</label>
                  <div>
                    <span class="badge bg-danger font-size-14 px-3 py-2">
                      <i class="mdi mdi-clock-outline me-1"></i>{{ billDetails.status }}
                    </span>
                  </div>
                </div>
              </div>

              <hr class="my-3">

              <div class="row">
                <!-- Customer Name -->
                <div class="col-md-6 mb-3">
                  <label class="text-muted mb-1 font-13px">Customer Name:</label>
                  <div class="fw-semibold">{{ billDetails.customerName || billDetails.description }}</div>
                </div>

                <!-- Phone Number -->
                <div class="col-md-6 mb-3">
                  <label class="text-muted mb-1 font-13px">Phone Number:</label>
                  <div class="fw-semibold">{{ maskPhoneNumber(billDetails.customerPhoneNumber) }}</div>
                </div>

                <!-- Location -->
                <div class="col-md-6 mb-3">
                  <label class="text-muted mb-1 font-13px">Location:</label>
                  <div>
                    <span class="badge bg-soft-info text-info me-2">{{ billDetails.subCountyName }}</span>
                    <span class="badge bg-soft-info text-info">{{ billDetails.wardName }}</span>
                  </div>
                </div>

                <!-- Created By -->
                <div class="col-md-6 mb-3">
                  <label class="text-muted mb-1 font-13px">Created By:</label>
                  <div class="fw-semibold">{{ billDetails.names }}</div>
                  <small class="text-muted">{{ formatDateTime(billDetails.dateCreated) }}</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Bill Items Table -->
          <div class="card border shadow-sm mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0 text-uppercase">
                <i class="mdi mdi-format-list-bulleted me-2"></i>Invoice Items
              </h6>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead class="table-light">
                  <tr>
                    <th style="width: 50px;" class="text-center">#</th>
                    <th style="min-width: 300px;">Description</th>
                    <th style="width: 100px;" class="text-center">Qty</th>
                    <th style="width: 150px;" class="text-end">Unit Price</th>
                    <th style="width: 150px;" class="text-end">Total</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(item, index) in billInfo" :key="index">
                    <td class="text-center">{{ index + 1 }}</td>
                    <td>
                      <div class="fw-semibold">{{ item.feeDescription }}</div>
                      <small v-if="item.customer" class="text-muted">{{ maskPhoneNumber(item.customer) }}</small>
                      <div v-if="item.description" class="mt-1">
                        <small class="text-muted">{{ item.description }}</small>
                      </div>
                    </td>
                    <td class="text-center">
                      <span class="badge bg-soft-primary text-primary">{{ item.quantity || 1 }}</span>
                    </td>
                    <td class="text-end">KES {{ numberFormat(item.billTotal / (item.quantity || 1)) }}</td>
                    <td class="text-end fw-bold text-primary">KES {{ numberFormat(item.billTotal) }}</td>
                  </tr>
                  </tbody>
                  <tfoot class="table-light">
                  <tr>
                    <td colspan="4" class="text-end fw-bold text-uppercase">Grand Total:</td>
                    <td class="text-end">
                      <h5 class="mb-0 text-success fw-bold">KES {{ numberFormat(billDetails.detailAmount) }}</h5>
                    </td>
                  </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          <!-- Payment Information (Only if printable is true) -->
          <div v-if="billDetails.printable === 'true'" class="card border-primary shadow-sm mb-4">
            <div class="card-header bg-soft-primary">
              <h6 class="mb-0 text-primary">
                <i class="mdi mdi-cash-multiple me-2"></i>Payment Information
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <!-- M-PESA Paybill -->
                <div class="col-md-6">
                  <h6 class="fw-semibold mb-3">
                    <i class="mdi mdi-cellphone-basic me-2"></i>M-PESA Payment
                  </h6>
                  <div class="bg-light p-3 rounded">
                    <div class="mb-2">
                      <small class="text-muted">Paybill Number:</small>
                      <div class="d-flex align-items-center">
                        <h5 class="mb-0 text-dark fw-bold me-2">{{ payBill.shortCode }}</h5>
                        <button
                            @click="copyToClipboard(payBill.shortCode)"
                            class="btn btn-sm btn-soft-primary"
                            title="Copy Paybill">
                          <i class="mdi mdi-content-copy"></i>
                        </button>
                      </div>
                    </div>
                    <div class="mb-2">
                      <small class="text-muted">Account Number:</small>
                      <div class="d-flex align-items-center">
                        <code class="text-dark fw-bold me-2">{{ billDetails.billNo }}</code>
                        <button
                            @click="copyToClipboard(billDetails.billNo)"
                            class="btn btn-sm btn-soft-primary"
                            title="Copy Account Number">
                          <i class="mdi mdi-content-copy"></i>
                        </button>
                      </div>
                    </div>
                    <div>
                      <small class="text-muted">Amount:</small>
                      <h6 class="mb-0 text-success fw-bold">KES {{ numberFormat(billDetails.billBalance) }}</h6>
                    </div>
                  </div>
                </div>

                <!-- Bank Payment -->
                <div class="col-md-6">
                  <h6 class="fw-semibold mb-3">
                    <i class="mdi mdi-bank me-2"></i>Bank Payment
                  </h6>
                  <div class="bg-light p-3 rounded">
                    <div class="mb-2">
                      <small class="text-muted">Bank Name:</small>
                      <div class="fw-semibold">{{ bank.bank }}</div>
                    </div>
                    <div class="mb-2">
                      <small class="text-muted">Account Number:</small>
                      <div class="d-flex align-items-center">
                        <code class="text-dark fw-bold me-2">{{ bank.payBill }}</code>
                        <button
                            @click="copyToClipboard(bank.payBill ? bank.payBill.toString() : '')"
                            class="btn btn-sm btn-soft-primary"
                            title="Copy Account Number">
                          <i class="mdi mdi-content-copy"></i>
                        </button>
                      </div>
                    </div>
                    <div>
                      <small class="text-muted">Reference:</small>
                      <code class="text-dark">{{ billDetails.billNo }}</code>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Payment Instructions -->
              <div class="mt-3 p-3 bg-soft-warning rounded">
                <h6 class="text-warning mb-2">
                  <i class="mdi mdi-information-outline me-2"></i>Payment Instructions
                </h6>
                <ol class="mb-0 ps-3 font-13px">
                  <li>Use the paybill number and account number provided above</li>
                  <li>Enter the exact amount shown: <strong>KES {{ numberFormat(billDetails.billBalance) }}</strong></li>
                  <li>Payment will be automatically confirmed</li>
                  <li>You will receive an SMS confirmation once payment is processed</li>
                </ol>
              </div>
            </div>
          </div>

          <!-- County Information -->
          <div class="card border shadow-sm">
            <div class="card-body">
              <div class="row align-items-center">
                <div class="col-md-2 text-center">
                  <img :src="county.logo" alt="County Logo" class="img-fluid" style="max-height: 80px;">
                </div>
                <div class="col-md-10">
                  <h6 class="mb-1">{{ county.countyNameFull }}</h6>
                  <p class="text-muted mb-1 font-13px">{{ county.tagline }}</p>
                  <div class="d-flex align-items-center font-13px">
                    <span class="text-muted me-3">
                      <i class="mdi mdi-phone me-1"></i>{{ county.contact || 'N/A' }}
                    </span>
                    <span class="text-muted">
                      <i class="mdi mdi-email me-1"></i>{{ county.email || 'N/A' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="modal-footer bg-light border-0">
          <div class="w-100 d-flex justify-content-between align-items-center">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              <i class="mdi mdi-close me-2"></i>Close
            </button>

            <div>
              <!-- Print Button (Only if printable) -->
              <button
                  v-if="billDetails.printable === 'true'"
                  @click="printBill"
                  type="button"
                  class="btn btn-info me-2">
                <i class="mdi mdi-printer me-2"></i>Print Invoice
              </button>

              <!-- Proceed to Payment Button (Only if printable) -->
              <button
                  v-if="billDetails.printable === 'true'"
                  @click="proceedToPayment"
                  type="button"
                  class="btn btn-success">
                <i class="mdi mdi-credit-card-outline me-2"></i>Proceed to Payment
              </button>

              <!-- Pending Approval Badge (If not printable) -->
              <span v-else class="badge bg-warning text-dark font-size-14 px-4 py-2">
                <i class="mdi mdi-clock-outline me-2"></i>Awaiting Approval
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment';

export default {
  name: 'BillGeneratedModal',

  props: {
    message: {
      type: String,
      default: null
    },
    billDetails: {
      type: Object,
      default: () => ({
        id: '',
        billNo: '',
        incomeTypeID: '',
        incomeTypeDescription: '',
        costCenterNo: '',
        accountNo: '',
        feeID: '',
        detailAmount: '',
        receiptAmount: '',
        billBalance: '',
        wardID: '',
        subCountyID: '',
        status: '',
        dateCreated: '',
        printable: 'false',
        customerName: '',
        customerPhoneNumber: '',
        subCountyName: '',
        wardName: '',
        names: '',
        description: ''
      })
    },
    billInfo: {
      type: Array,
      default: () => []
    },
    bank: {
      type: Object,
      default: () => ({
        id: null,
        bankID: null,
        bank: '',
        payBill: '',
        format: '',
        bankType: '',
        accountNo: null,
        company: ''
      })
    },
    payBill: {
      type: Object,
      default: () => ({
        id: null,
        stream: '',
        shortCode: ''
      })
    },
    county: {
      type: Object,
      default: () => ({
        id: null,
        countyID: '',
        countyName: '',
        countyNameFull: '',
        tagline: '',
        contact: '',
        email: '',
        logo: '',
        stateLogo: '',
        signature: '',
        poweredByLogo: ''
      })
    },
    // ✅ NEW: Pay Later props
    payLater: {
      type: Boolean,
      default: false
    },
    customerPhone: {
      type: String,
      default: ''
    },
    customerEmail: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      copiedText: null
    };
  },

  methods: {
    numberFormat(num) {
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(num);
    },

    formatDateTime(dateString) {
      if (!dateString) return '';
      return moment(dateString).format('DD MMM YYYY, hh:mm A');
    },

    maskPhoneNumber(input) {
      if (!input || typeof input !== 'string') {
        return input;
      }

      const digits = input.replace(/\D/g, '');

      if (digits.length > 4) {
        return input.slice(0, 2) + input.slice(2, -2).replace(/\d/g, '*') + input.slice(-2);
      }

      return input;
    },

    maskBillNo(billNo, printable) {
      if (!billNo) return '';

      // If printable is false, mask the bill number
      if (printable === 'false') {
        const prefix = billNo.substring(0, 4);
        const suffix = billNo.substring(billNo.length - 3);
        const masked = '*'.repeat(billNo.length - 7);
        return `${prefix}${masked}${suffix}`;
      }

      return billNo;
    },

    copyToClipboard(text) {
      if (!text) return;

      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);

      textarea.select();
      document.execCommand('copy');

      document.body.removeChild(textarea);

      this.$emit('show-toast', {
        type: 'success',
        message: `Copied: ${text}`
      });

      console.log('Copied to clipboard:', text);
    },

    printBill() {
      if (this.billDetails.printable === 'false') {
        alert('This invoice must be approved before it can be printed.');
        return;
      }
      this.$emit('print-bill');
    },

    proceedToPayment() {
      if (this.billDetails.printable === 'false') {
        alert('This invoice must be approved before payment can be processed.');
        return;
      }

      console.log('✅ Proceeding to payment...');

      const modalEl = document.getElementById('bill-generated-modal');
      if (modalEl) {
        if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
          const modalInstance = bootstrap.Modal.getInstance(modalEl);
          if (modalInstance) {
            modalInstance.hide();
          }
        } else if (typeof $ !== 'undefined') {
          $(modalEl).modal('hide');
        } else {
          modalEl.classList.remove('show');
          modalEl.style.display = 'none';
          modalEl.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('modal-open');
          const backdrop = document.querySelector('.modal-backdrop');
          if (backdrop) backdrop.remove();
        }
      }

      setTimeout(() => {
        this.$emit('proceed-to-payment');
      }, 300);
    },

    closeModal() {
      const modalEl = document.getElementById('bill-generated-modal');
      if (modalEl) {
        if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
          const modalInstance = bootstrap.Modal.getInstance(modalEl);
          if (modalInstance) {
            modalInstance.hide();
          }
        } else if (typeof $ !== 'undefined') {
          $(modalEl).modal('hide');
        } else {
          modalEl.classList.remove('show');
          modalEl.style.display = 'none';
          modalEl.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('modal-open');
          const backdrop = document.querySelector('.modal-backdrop');
          if (backdrop) backdrop.remove();
        }
      }
    }
  }
};
</script>

<style scoped>
.modal-xl {
  max-width: 1200px;
}

.font-13px {
  font-size: 13px;
}

.bg-soft-primary {
  background-color: rgba(85, 110, 230, 0.18);
}

.bg-soft-success {
  background-color: rgba(52, 195, 143, 0.18);
}

.bg-soft-warning {
  background-color: rgba(241, 180, 76, 0.18);
}

.bg-soft-info {
  background-color: rgba(80, 165, 241, 0.18);
}

.badge {
  font-weight: 500;
  padding: 0.35rem 0.65rem;
}

.table-hover tbody tr:hover {
  background-color: rgba(85, 110, 230, 0.05);
}

.card {
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

code {
  padding: 0.2rem 0.4rem;
  font-size: 90%;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
}

.btn-soft-primary {
  background-color: rgba(85, 110, 230, 0.18);
  color: #556ee6;
  border: none;
}

.btn-soft-primary:hover {
  background-color: rgba(85, 110, 230, 0.3);
  color: #556ee6;
}

.modal.fade .modal-dialog {
  transition: transform 0.3s ease-out;
}

@media print {
  .modal-header,
  .modal-footer,
  .btn {
    display: none !important;
  }
}
</style>